// @flow
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
	MultiCanvasDisplay,
	TeamCanvasDisplay,
	TeamHeader,
	CanvasContainer,
} from './MultiCanvasDisplay'
import type { CollaborativeCulminatingMomentScreen } from '@mission.io/mission-toolkit'
import type {
	VideoWithText,
	ImageWithText,
} from '@mission.io/mission-toolkit/actions'
import { COLLABORATIVE_CULMINATING_MOMENT } from '@mission.io/mission-toolkit/constants'
import { styled, css, keyframes } from 'styled-components'
import { useSendWebsocketMessage } from '../../../../context/provider'
import { ONE_SECOND, cssPlayerHeightUnit } from '../../../../utility/constants'
import ConnectedVideo from '../../../basics/ConnectedVideo'
import { useLocalTimers } from '../../../../context/timers'
import {
	useJuniorPlusStateSelector,
	useMissionStateSelector,
} from '../../../../context/customHooks'
import {
	getNextDocumentToShow,
	getTeams,
	isPaused,
} from '../../../../context/missionState/missionStats'
import {
	CCM_FAIL_VIDEO_URL,
	CCM_SUCCESS_VIDEO_URL,
} from '../../../../assets/constants'
import { useTeam } from './CollaborativeCulminatingMomentGrading'
import { fadeOut } from './keyframes'
import { CollaborativeCulminatingMomentWrapper } from '../sharedComponents'

type CurrentMedia = {
	success: boolean,
	media: null | VideoWithText | ImageWithText,
	documentId: string,
}

export type DisplayResultsStep = {
	status: typeof COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.DISPLAY_RESULTS,
	qualifiedDocuments: {
		documentId: string,
	}[],
	currentMedia: Array<CurrentMedia>,
}

type Props = {
	screen: CollaborativeCulminatingMomentScreen,
	displayStep: DisplayResultsStep,
}

/**
 * Handles logic and determines specific props needed to display the different types of endings for the collaborative culminating moment screen.
 */
export function CollaborativeCulminatingMomentDisplayResults({
	screen,
	displayStep,
}: Props): React$Node {
	const sendWebsocketMessage = useSendWebsocketMessage()
	const canvas = useMissionStateSelector(
		// $FlowFixMe[prop-missing] TODO - creativeCanvas on the junior state in mission-toolkit does not have `canvases` and is not exact, so we get a flow error here
		state => state?.stationData.creativeCanvas.canvases?.[screen.canvasId]
	)
	const documentIdToTeamId = React.useMemo(() => {
		if (!canvas) return {}
		const documentIdToTeamId = {}

		for (const team of canvas.teams) {
			documentIdToTeamId[team.document.id] = team.teamId
		}

		return documentIdToTeamId
	}, [canvas])

	const onComplete = useCallback(
		(documentIds: string[]) => {
			sendWebsocketMessage({
				type: 'COLLABORATIVE_CULMINATING_MOMENT_FINISH_DOCUMENT_ANIMATION',
				payload: {
					screenId: screen._actionId,
					documentIds: documentIds,
				},
			})
		},
		[sendWebsocketMessage, screen._actionId]
	)

	// If the screen ending is an each response ending, we need to display the media
	if (
		screen.ending === COLLABORATIVE_CULMINATING_MOMENT.ENDING.EACH_RESPONSE ||
		displayStep.currentMedia.length === 1
	) {
		return (
			<CollaborativeCulminatingMomentWrapper>
				<PlayFullScreenSingleResult
					documentIdToTeamId={documentIdToTeamId}
					currentMedia={displayStep.currentMedia[0]}
					canvasId={screen.canvasId}
					onComplete={onComplete}
					voteReveal={
						screen.ending === COLLABORATIVE_CULMINATING_MOMENT.ENDING.CLASS_VOTE
					}
				/>
			</CollaborativeCulminatingMomentWrapper>
		)
	}
	// If the screen ending is an all responses ending, we need to display all the media
	if (screen.ending === COLLABORATIVE_CULMINATING_MOMENT.ENDING.ALL_RESPONSES) {
		return (
			<CollaborativeCulminatingMomentWrapper>
				<PlayAllResultsTogether
					screen={screen}
					currentMedia={displayStep.currentMedia}
					onComplete={onComplete}
					documentIdToTeamId={documentIdToTeamId}
				/>
			</CollaborativeCulminatingMomentWrapper>
		)
	}
}

/**
 * The reveal for a collaborative culminating moment EACH_RESPONSE result experience. Meant to feel dramatic and reveal to a class a single canvas and whether it is correct or not.
 * @param {{[string]: string}} props.documentIdToTeamId a map to enable lookup of teamId from documentId
 * @param {CurrentMedia} props.currentMedia the current media to display
 * @param {string} props.canvasId the canvasId to display the media on
 * @param {(documentId: string[]) => void} props.onComplete a callback to call when the media has finished displaying
 * @returns
 */
function PlayFullScreenSingleResult({
	documentIdToTeamId,
	currentMedia,
	canvasId,
	onComplete,
	voteReveal,
}: {
	documentIdToTeamId: { [string]: string },
	currentMedia: CurrentMedia,
	canvasId: string,
	onComplete: (documentId: string[]) => void,
	voteReveal: boolean,
}) {
	const teams = useJuniorPlusStateSelector(getTeams)

	const singleTeam = teams?.find(
		team => team.id === documentIdToTeamId[currentMedia?.documentId]
	)
	const [fadeOut, setFadeOut] = useState(false)

	const onCompleteTimeoutRef = useRef()

	const _onComplete = useCallback(() => {
		setFadeOut(true)
		onCompleteTimeoutRef.current = setTimeout(() => {
			onComplete([currentMedia.documentId])
		}, FADE_OUT_TIME)
	}, [onComplete, currentMedia.documentId])

	useEffect(() => {
		setFadeOut(false)
		return () => {
			clearTimeout(onCompleteTimeoutRef.current)
		}
	}, [currentMedia])

	return (
		<SingleResult
			fullScreen
			canvasId={canvasId}
			teamId={singleTeam?.id}
			document={currentMedia}
			delay={voteReveal ? 0.5 : 4}
			fadeOut={fadeOut}
			onComplete={_onComplete}
		/>
	)
}
const FADE_OUT_TIME = 1 * ONE_SECOND
/**
 * The reveal for a collaborative culminating moment ALL_RESPONSES result experience. Meant to reveal all canvases at once and whether they are correct or not.
 * @param {{[string]: string}} props.documentIdToTeamId a map to enable lookup of teamId from documentId
 * @param {CurrentMedia[]} props.currentMedia the current media to display
 * @param {string} props.canvasId the canvasId to display the media on
 * @param {(documentId: string[]) => void} props.onComplete a callback to call when the media has finished displaying
 */
function PlayAllResultsTogether({
	screen,
	currentMedia,
	onComplete,
	documentIdToTeamId,
}: {
	screen: CollaborativeCulminatingMomentScreen,
	currentMedia: Array<CurrentMedia>,
	onComplete: (documentId: string[]) => void,
	documentIdToTeamId: { [string]: string },
}): React$Node {
	const [fadeOut, setFadeOut] = useState(false)

	const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0)
	const completedDocuments = useRef(new Set())

	const teams = useJuniorPlusStateSelector(getTeams)

	const _onComplete = useCallback(
		(documentId: string) => {
			setCurrentDocumentIndex(
				currentMedia.findIndex(m => m.documentId === documentId) + 1
			)
			completedDocuments.current.add(documentId)
			if (
				currentMedia.every(({ documentId }) =>
					completedDocuments.current.has(documentId)
				)
			) {
				setFadeOut(true)
				setTimeout(() => {
					onComplete(Array.from(completedDocuments.current))
				}, FADE_OUT_TIME)
			}
		},
		[onComplete, currentMedia]
	)

	return (
		<StyledMultiCanvasDisplay>
			{currentMedia.map((document, i) => {
				const team = teams?.find(
					team => team.id === documentIdToTeamId[document.documentId]
				)
				return (
					<SingleResult
						delay={i === 0 ? 4 : 0}
						key={document.documentId}
						canvasId={screen.canvasId}
						teamId={team?.id}
						document={document}
						canStartReveal={currentDocumentIndex === i}
						fadeOut={fadeOut}
						onComplete={_onComplete}
					/>
				)
			})}
		</StyledMultiCanvasDisplay>
	)
}

/**
 * Displays a single document and its result.
 * @param props
 * @param props.document The document to display.
 * @param props.delay How long to wait in seconds before displaying the result.
 * @param props.showMedia true if the media should be displayed, false if the result reveal should be displayed.
 * @param props.onFinishReveal A callback to call when the result reveal has finished displaying.
 * @param props.onComplete A callback to call when the media has finished displaying.
 */
function SingleResult({
	canvasId,
	canStartReveal = true,
	document,
	delay,
	fadeOut,
	onComplete,
	teamId,
	fullScreen,
}: {|
	canvasId: string,
	canStartReveal?: boolean,
	document: CurrentMedia,
	delay?: number,
	fadeOut: boolean,
	onComplete: (documentId: string) => void,
	teamId: ?string,
	fullScreen?: boolean,
|}) {
	const team = useTeam(teamId)
	const resultOnEnd = useCallback(() => {
		onComplete(document?.documentId)
	}, [onComplete, document?.documentId])

	const nextDocument = useJuniorPlusStateSelector(getNextDocumentToShow)

	const [hideMedia, setHideMedia] = useState(false)
	const [showResult, setShowResult] = useState(false)
	const documentToShow = hideMedia ? nextDocument || document : document

	useEffect(() => {
		setShowResult(false)
		setHideMedia(false)
	}, [document])

	return (
		<PossiblyFullScreen
			$fullscreen={fullScreen}
			data-testid={`canvas-document-container-${document.documentId}`}
			key={document.documentId}
			className={fadeOut ? 'fade-out' : ''}>
			{team && (
				<TeamHeader team={team} className={fullScreen ? 'mt-3 !z-[3]' : ''} />
			)}

			{documentToShow && (
				<StyledCanvasContainer
					className={hideMedia ? 'fade-out' : ''}
					$fullscreen={fullScreen}
					canvasId={canvasId}
					documentId={documentToShow.documentId}
				/>
			)}

			{canStartReveal && documentToShow && (
				<ResultMedia
					className={[
						'overlay bg-black !w-fit z-[0]',
						hideMedia ? 'fade-out' : '',
					]
						.filter(Boolean)
						.join(' ')}
					delay={delay}
					media={document.media}
					onEnd={() => setShowResult(true)}
				/>
			)}
			{showResult && (
				<ResultReveal
					staggered={!fullScreen}
					className="overlay !w-fit z-[2]"
					onEndEntrance={() => setHideMedia(true)}
					onEnd={resultOnEnd}
					success={document.success}
				/>
			)}
		</PossiblyFullScreen>
	)
}

const PossiblyFullScreen = styled(TeamCanvasDisplay)`
	.fade-out,
	&.fade-out {
		animation: ${fadeOut} ${FADE_OUT_TIME}ms linear both;
	}

	.overlay {
		height: calc(100% - var(--header-height) + var(--spacing));
		width: 100%;
		transform: translateX(-50%);
		position: absolute;
		border-radius: var(--spacing);
		border: 2px solid aqua;
		bottom: 0;
		left: 50%;
	}

	${({ $fullscreen }) =>
		$fullscreen &&
		css`
			position: static;
			height: 100%;
			width: 100%;
			max-width: 100%;
			max-height: 100%;

			.overlay {
				transform: translate(-50%, -50%);
				top: 50%;
			}
		`}
`

/**
 * Displays the result of a document, which includes a success or failure indicator based on `success`.
 * @param props
 * @param props.success Whether the document should be considered a success.
 * @param props.delay How long to wait before displaying the result.
 */
function ResultReveal({
	staggered,
	success,
	className,
	onEndEntrance,
	onEnd,
}: {
	success: boolean,
	staggered?: boolean,
	className?: string,
	onEndEntrance: () => void,
	onEnd: () => void,
	muted?: boolean,
}): React$Node {
	const videoRef = useRef()
	const endedRef = useRef<{ timer?: TimeoutID, finished?: boolean }>({})

	// For a staggered video effect (where the video fades out while the next video fades in) we want to call the onEnd function when the video has played for 5 seconds
	const onStartVideo = useCallback(() => {
		if (staggered) {
			if (endedRef.current?.timer || endedRef.current?.finished) {
				// if the video has ended we don't want to start the timer
				return
			}
			endedRef.current = {
				timer: setTimeout(() => {
					delete endedRef.current?.timer
					endedRef.current.finished = true
					onEnd()
				}, 5 * ONE_SECOND),
			}
			return () => {
				clearTimeout(endedRef.current?.timer)
			}
		}
	}, [onEnd, staggered])

	return (
		<AnimatedEntranceContainer
			{...{
				onAnimationEnd: onEndEntrance,
				// If the animation cancels we want to call the callback anyway
				onAnimationCancel: onEndEntrance,
			}}>
			<VideoFillContainer
				aria-label={success ? 'Success' : 'Failure'}
				ref={videoRef}
				className={[className, '!border-transparent'].filter(Boolean).join(' ')}
				url={success ? CCM_SUCCESS_VIDEO_URL : CCM_FAIL_VIDEO_URL}
				onPlay={onStartVideo}
				onEnded={() => {
					if (
						staggered &&
						endedRef.current.timer &&
						!endedRef.current.finished
					) {
						clearTimeout(endedRef.current.timer)
						endedRef.current.finished = true
						endedRef.current.timer = undefined
						onEnd()
					} else if (!staggered) {
						onEnd()
					}
				}}
			/>
		</AnimatedEntranceContainer>
	)
}

const IMAGE_MEDIA_REVIEW_DURATION = 3 * ONE_SECOND

/**
 * Displays the media for a result. If the media is a video, `onEnd` will be called when the video finishes playing.
 * If the media is an image, `onEnd` will be called after `IMAGE_MEDIA_REVIEW_DURATION` milliseconds.
 */
function ResultMedia({
	media,
	className,
	delay = 0,
	onEnd,
	muted = false,
}: {
	media: CurrentMedia['media'],
	className?: string,
	onEnd: () => mixed,
	delay?: number,
	muted?: boolean,
}): React$Node {
	const missionIsPaused = useMissionStateSelector(isPaused)
	const timers = useLocalTimers(missionIsPaused)
	const videoRef = useRef()
	const imageRef = useRef()

	// Call onEnd for images after a timer
	const addImageTimer = () => {
		timers.addTimer(onEnd, IMAGE_MEDIA_REVIEW_DURATION)
	}

	// Call onEnd immediately if the media is not an image or video
	useEffect(() => {
		if (media?.type !== 'VIDEO' && media?.type !== 'IMAGE') {
			if (delay) {
				let timeoutId = setTimeout(onEnd, delay * ONE_SECOND)
				return () => {
					clearTimeout(timeoutId)
				}
			} else {
				onEnd()
			}
		}
	}, [media, onEnd, delay])
	return (
		<AnimatedEntranceContainer
			$delay={delay || 0}
			onAnimationEnd={() => {
				if (videoRef.current) {
					videoRef.current?.play()
				} else if (imageRef.current) {
					addImageTimer()
				}
			}}
			onAnimationCancel={() => {
				videoRef.current?.play()
			}}>
			{media?.type === 'IMAGE' ? (
				<ImageFillContainer
					ref={imageRef}
					className={className}
					src={media.url}
					alt={media.text}
					draggable={false}
				/>
			) : media?.type === 'VIDEO' ? (
				// $FlowExpectedError[incompatible-use] styled components does not type refs correctly when the component being styled is a component using forwardRef
				<ContainerVideo
					aria-label={media.text}
					autoPlay={false}
					ref={videoRef}
					className={className}
					url={media.url}
					muted={muted}
					onEnded={onEnd}
				/>
			) : null}
		</AnimatedEntranceContainer>
	)
}

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`

const AnimatedEntranceContainer = styled.div`
	animation: ${fadeIn} 2s;
	animation-delay: ${({ $delay }) => $delay}s;
	animation-fill-mode: backwards; // begin with opacity 0
`

const StyledMultiCanvasDisplay = styled(MultiCanvasDisplay)`
	padding: var(--spacing-4x) var(--spacing-2x);
`

const StyledCanvasContainer = styled(CanvasContainer)`
	z-index: -1;
	${({ $fullscreen }) =>
		$fullscreen
			? `
							width: ${cssPlayerHeightUnit(100)};
							height: ${cssPlayerHeightUnit(75)};
						`
			: ''}
`

const ContainerVideo = styled(ConnectedVideo)`
	object-fit: contain;
`

const VideoFillContainer = styled(ConnectedVideo)`
	object-fit: cover;
	background-color: transparent;
`

const ImageFillContainer = styled.img`
	object-fit: contain;
	background-color: black;
`
